/**
 * Messages of the design-system additions to the kit (plan §9.1): theme, object colours and the
 * wave-2 primitives (Stepper, DiffView, Sparkline, Meter, CopyField, StatusPill, KeyValueList).
 * Same rules as `primitives.ts`: one `<area>En` / `<area>De` pair per primitive, registered by the
 * component's module; `de` has every key of `en` (type-checked). Owner: design-system.
 */
import { registerPrimitiveMessages } from './primitives';

type De<E> = Record<keyof E, string>;

export const themeEn = {
  'ui.theme.label': 'Theme',
  'ui.theme.light': 'Light',
  'ui.theme.dark': 'Dark',
  'ui.theme.system': 'System',
} as const;
export const themeDe: De<typeof themeEn> = {
  'ui.theme.label': 'Design',
  'ui.theme.light': 'Hell',
  'ui.theme.dark': 'Dunkel',
  'ui.theme.system': 'System',
};

export const objectColorEn = {
  'ui.objectColor.indigo': 'Indigo',
  'ui.objectColor.blue': 'Blue',
  'ui.objectColor.teal': 'Teal',
  'ui.objectColor.green': 'Green',
  'ui.objectColor.amber': 'Amber',
  'ui.objectColor.orange': 'Orange',
  'ui.objectColor.rose': 'Rose',
  'ui.objectColor.violet': 'Violet',
  'ui.objectColor.slate': 'Slate',
} as const;
export const objectColorDe: De<typeof objectColorEn> = {
  'ui.objectColor.indigo': 'Indigo',
  'ui.objectColor.blue': 'Blau',
  'ui.objectColor.teal': 'Petrol',
  'ui.objectColor.green': 'Grün',
  'ui.objectColor.amber': 'Bernstein',
  'ui.objectColor.orange': 'Orange',
  'ui.objectColor.rose': 'Rosé',
  'ui.objectColor.violet': 'Violett',
  'ui.objectColor.slate': 'Schiefer',
};

export const stepperEn = {
  'ui.stepper.complete': 'Completed',
  'ui.stepper.current': 'Current step',
  'ui.stepper.upcoming': 'Not started',
  'ui.stepper.error': 'Needs attention',
  'ui.stepper.skipped': 'Skipped',
  'ui.stepper.progress': 'Step {n} of {total}',
} as const;
export const stepperDe: De<typeof stepperEn> = {
  'ui.stepper.complete': 'Erledigt',
  'ui.stepper.current': 'Aktueller Schritt',
  'ui.stepper.upcoming': 'Noch nicht begonnen',
  'ui.stepper.error': 'Braucht Aufmerksamkeit',
  'ui.stepper.skipped': 'Übersprungen',
  'ui.stepper.progress': 'Schritt {n} von {total}',
};

export const diffEn = {
  'ui.diff.added': 'Added',
  'ui.diff.removed': 'Removed',
  'ui.diff.changed': 'Changed',
  'ui.diff.moved': 'Moved',
  'ui.diff.count.added_one': '{count} added',
  'ui.diff.count.added_other': '{count} added',
  'ui.diff.count.removed_one': '{count} removed',
  'ui.diff.count.removed_other': '{count} removed',
  'ui.diff.count.changed_one': '{count} changed',
  'ui.diff.count.changed_other': '{count} changed',
  'ui.diff.count.moved_one': '{count} moved',
  'ui.diff.count.moved_other': '{count} moved',
  'ui.diff.before': 'Before',
  'ui.diff.after': 'After',
  'ui.diff.showAll_one': 'Show {count} change',
  'ui.diff.showAll_other': 'Show all {count} changes',
  'ui.diff.filter': 'Show changes',
  'ui.diff.all': 'All',
  'ui.diff.none': 'No changes',
  'ui.diff.group': '{group}, {count} changes',
  'ui.diff.group_one': '{group}, 1 change',
  'ui.diff.details': 'Show details',
  'ui.diff.hideDetails': 'Hide details',
} as const;
export const diffDe: De<typeof diffEn> = {
  'ui.diff.added': 'Hinzugefügt',
  'ui.diff.removed': 'Entfernt',
  'ui.diff.changed': 'Geändert',
  'ui.diff.moved': 'Verschoben',
  'ui.diff.count.added_one': '{count} hinzugefügt',
  'ui.diff.count.added_other': '{count} hinzugefügt',
  'ui.diff.count.removed_one': '{count} entfernt',
  'ui.diff.count.removed_other': '{count} entfernt',
  'ui.diff.count.changed_one': '{count} geändert',
  'ui.diff.count.changed_other': '{count} geändert',
  'ui.diff.count.moved_one': '{count} verschoben',
  'ui.diff.count.moved_other': '{count} verschoben',
  'ui.diff.before': 'Vorher',
  'ui.diff.after': 'Nachher',
  'ui.diff.showAll_one': '{count} Änderung anzeigen',
  'ui.diff.showAll_other': 'Alle {count} Änderungen anzeigen',
  'ui.diff.filter': 'Änderungen zeigen',
  'ui.diff.all': 'Alle',
  'ui.diff.none': 'Keine Änderungen',
  'ui.diff.group': '{group}, {count} Änderungen',
  'ui.diff.group_one': '{group}, 1 Änderung',
  'ui.diff.details': 'Details anzeigen',
  'ui.diff.hideDetails': 'Details ausblenden',
};

export const chartEn = {
  'ui.chart.trend': '{label}: from {first} to {last}, low {min}, high {max}',
  'ui.chart.trendEmpty': '{label}: no data',
  'ui.chart.point': '{x}: {value}',
  'ui.chart.pointN': 'Point {n} of {total}: {value}',
  'ui.chart.explore': 'Use the arrow keys to read the values.',
  'ui.meter.of': '{value} of {max}',
} as const;
export const chartDe: De<typeof chartEn> = {
  'ui.chart.trend': '{label}: von {first} auf {last}, Tief {min}, Hoch {max}',
  'ui.chart.trendEmpty': '{label}: keine Daten',
  'ui.chart.point': '{x}: {value}',
  'ui.chart.pointN': 'Punkt {n} von {total}: {value}',
  'ui.chart.explore': 'Mit den Pfeiltasten die Werte lesen.',
  'ui.meter.of': '{value} von {max}',
};

export const copyEn = {
  'ui.copy.copy': 'Copy',
  'ui.copy.copyNamed': 'Copy {label}',
  'ui.copy.copied': 'Copied to the clipboard',
  'ui.copy.copiedShort': 'Copied',
  'ui.copy.failed': 'Could not copy. Select the text and press {shortcut}.',
  'ui.copy.reveal': 'Show',
  'ui.copy.hide': 'Hide',
} as const;
export const copyDe: De<typeof copyEn> = {
  'ui.copy.copy': 'Kopieren',
  'ui.copy.copyNamed': '{label} kopieren',
  'ui.copy.copied': 'In die Zwischenablage kopiert',
  'ui.copy.copiedShort': 'Kopiert',
  'ui.copy.failed': 'Kopieren nicht möglich. Text markieren und {shortcut} drücken.',
  'ui.copy.reveal': 'Anzeigen',
  'ui.copy.hide': 'Verbergen',
};

export const bulkEn = {
  'ui.bulk.selected': '{count} selected',
  'ui.bulk.label': 'Bulk actions',
  'ui.bulk.more': 'More',
  'ui.bulk.clear': 'Clear selection',
  'ui.bulk.clearHint': 'Clear selection (Esc)',
} as const;
export const bulkDe: De<typeof bulkEn> = {
  'ui.bulk.selected': '{count} ausgewählt',
  'ui.bulk.label': 'Sammelaktionen',
  'ui.bulk.more': 'Mehr',
  'ui.bulk.clear': 'Auswahl aufheben',
  'ui.bulk.clearHint': 'Auswahl aufheben (Esc)',
};

export const keyValueEn = {
  'ui.kv.empty': 'Empty',
} as const;
export const keyValueDe: De<typeof keyValueEn> = {
  'ui.kv.empty': 'Leer',
};

export type DesignMessageKey =
  | keyof typeof themeEn
  | keyof typeof objectColorEn
  | keyof typeof stepperEn
  | keyof typeof diffEn
  | keyof typeof chartEn
  | keyof typeof copyEn
  | keyof typeof bulkEn
  | keyof typeof keyValueEn;

/** Every pair, for parity tests and tools. */
export const ALL_DESIGN_MESSAGES = [
  [themeEn, themeDe],
  [objectColorEn, objectColorDe],
  [stepperEn, stepperDe],
  [diffEn, diffDe],
  [chartEn, chartDe],
  [copyEn, copyDe],
  [bulkEn, bulkDe],
  [keyValueEn, keyValueDe],
] as const;

export { registerPrimitiveMessages as registerDesignMessages };
